/**
 * Afrikaans: Compose
 *
 * What this file covers: Writing a single message to one person or a few, outside a conversation.
 *
 * Tone: Friendly and direct. Make clear who will receive the message and on which channel.
 *
 * Afrikaans: Standard South African Afrikaans. Address the reader as "jy". Use the same Afrikaans word for the
 * same English term everywhere; menu.ts and common.ts set the words the other files follow. Afrikaans must stay
 * complete: the release checks fail if a key is missing.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const compose: Catalog = {
  'compose.title': 'Nuwe boodskap',
  'compose.description': 'Stuur \'n boodskap aan een persoon, of \'n paar. Kies hulle op naam of nommer, en die antwoord land in jou inmandjie.',
  'compose.step.who': 'Vir wie dit is',
  'compose.step.what': 'Wat dit sê',
  'compose.step.send': 'Koste en stuur',
  'compose.number.label': 'Stuur vanaf',
  'compose.number.hint': 'Die nommer waarvandaan hierdie boodskap kom. Die antwoord land in daardie nommer se inmandjie.',
  'compose.recipients.label': 'Aan',
  'compose.recipients.hint': 'Soek \'n naam of \'n nommer, of tik \'n nommer wat nog nie gestoor is nie.',
  'compose.recipients.placeholder': 'Naam of nommer',
  'compose.recipients.remove': 'Verwyder {{who}}',
  'compose.recipients.notInBook': 'Nog nie in jou kontakte nie',
  'compose.recipients.noName': 'Geen naam gestoor nie',
  'compose.recipients.noMatches': 'Niemand pas daarby nie. Tik \'n volledige nommer om iemand nuuts te stuur.',
  'compose.recipients.count': '{{count}} van {{max}} mense gekies',
  'compose.recipients.clear': 'Maak almal skoon',
  'compose.recipients.full.title': 'Dis soveel as wat hierdie een vat',
  'compose.recipients.full.body': '\'n Boodskap wat hier geskryf word, gaan na hoogstens {{max}} mense. Vir meer as dit, stuur \'n uitsending na \'n etiket of \'n segment.',
  'compose.window.badge.open': 'Kan \'n boodskap ontvang',
  'compose.window.badge.needsTemplate': 'Benodig \'n sjabloon',
  'compose.window.badge.templateOnly': 'Slegs sjabloon',
  'compose.window.badge.optedOut': 'Uitgeteken',
  'compose.window.why.openHours': 'Het aan jou geskryf, {{hours}} h oor om vryelik te antwoord',
  'compose.window.why.openMinutes': 'Het aan jou geskryf, {{minutes}} min oor om vryelik te antwoord',
  'compose.window.why.never': 'Het nog nooit aan jou geskryf nie',
  'compose.window.why.expiredDays': 'Laas {{days}} dae gelede van hulle gehoor',
  'compose.window.why.expiredHours': 'Laas {{hours}} uur gelede van hulle gehoor',
  'compose.window.why.optedOut': 'Het gevra om nie boodskappe te ontvang nie',
  'compose.window.closed.title': 'Hierdie een benodig \'n goedgekeurde sjabloon',
  'compose.window.closed.body': 'WhatsApp laat net \'n boodskap in jou eie woorde toe binne 24 uur nadat iemand aan jou geskryf het. Kies eerder \'n goedgekeurde sjabloon.',
  'compose.window.mixed.title': 'Hierdie mense is nie almal in dieselfde posisie nie',
  'compose.window.mixed.body': '{{closed}} van hierdie {{total}} het nie in die laaste 24 uur aan jou geskryf nie, en een boodskap gaan na almal van hulle, dus moet dit \'n goedgekeurde sjabloon wees.',
  'compose.window.optedOut': '{{count}} van die mense wat gekies is, het gevra om nie boodskappe te ontvang nie. Hulle word uitgelaat en nie gehef nie.',
  'compose.message.label': 'Boodskap',
  'compose.message.hint': 'In jou eie woorde. Tik {{ om iets oor elke persoon by te voeg, soos hul naam.',
  'compose.message.placeholder': 'Skryf jou boodskap',
  'compose.message.count': '{{length}} van {{max}}',
  'compose.message.previewEmpty': 'Jou boodskap sal hier wys',
  'compose.message.previewBusiness': 'Jou besigheid',
  'compose.template.label': 'Sjabloon',
  'compose.template.hint': 'Slegs sjablone wat WhatsApp goedgekeur het, kan buite die 24-uur-venster gestuur word.',
  'compose.template.placeholders': 'Vul die oop plekke in',
  'compose.template.placeholders.hint': 'Tik {{ om ’n oop plek uit elke persoon se kontakkaart in te vul, soos hul naam. Elke persoon kry dan hul eie waarde.',
  'compose.template.placeholder': 'Oop plek {{n}}',
  'compose.template.noPlaceholders': 'Hierdie sjabloon het geen oop plekke om in te vul nie.',
  'compose.template.noneApproved.title': 'Nog geen goedgekeurde sjablone nie',
  'compose.template.noneApproved.body': 'WhatsApp moet \'n sjabloon goedkeur voordat dit gestuur kan word aan iemand wat nie onlangs aan jou geskryf het nie.',
  'compose.template.noneApproved.action': 'Skep \'n sjabloon',
  'compose.template.devFallbackHidden': '{{count}} sjablone is versteek omdat WhatsApp hulle nog nie goedgekeur het nie.',
  'compose.summary.recipients': 'Mense',
  'compose.summary.excluded': '{{count}} uitgelaat',
  'compose.summary.cost': 'Dit sal kos',
  'compose.summary.balance': 'Balans',
  'compose.summary.balanceAfter': '{{after}} oor ná die stuur',
  'compose.summary.balanceUnknown': 'Kies vir wie dit gaan, dan werk ons dit uit.',
  'compose.summary.short.title': 'Nie genoeg krediete nie',
  'compose.summary.short.body': 'Jy benodig {{shortfall}} meer om dit te stuur.',
  'compose.summary.short.action': 'Laai op',
  'compose.summary.broadcastHint': 'Stuur jy aan baie mense?',
  'compose.summary.broadcastAction': 'Stuur \'n uitsending',
  'compose.action.send': 'Stuur aan {{count}}',
  'compose.action.sendTemplate': 'Stuur sjabloon aan {{count}}',
  'compose.error.bodyRequired': 'Skryf die boodskap voordat jy dit stuur.',
  'compose.error.templateRequired': 'Kies \'n goedgekeurde sjabloon.',
  'compose.error.placeholderRequired': 'Vul dit in voordat jy stuur.',
  'compose.error.numbersFailed': 'Jou WhatsApp-nommers kon nie nou gelaai word nie.',
  'compose.error.noNumbers.title': 'Geen WhatsApp-nommer gekoppel nie',
  'compose.error.noNumbers.body': '\'n Boodskap moet van \'n gekoppelde nommer af kom. Koppel een, dan kan jy enigiemand \'n boodskap stuur.',
  'compose.error.noNumbers.action': 'Koppel \'n nommer',
  'compose.refusal.windowClosed': 'Sommige van hierdie mense het nie in die laaste 24 uur aan jou geskryf nie, dus moet dit \'n goedgekeurde sjabloon wees. Niks is gestuur nie.',
  'compose.refusal.TOO_MANY_RECIPIENTS': 'Dis meer mense as wat \'n boodskap wat hier geskryf word, vat. Stuur eerder \'n uitsending na \'n etiket of \'n segment.',
  'compose.refusal.NUMBER_REQUIRED': 'Sê vanaf watter van jou nommers hierdie boodskap kom.',
  'compose.refusal.NO_ACTIVE_NUMBER': 'Geen WhatsApp-nommer is nog gekoppel nie, dus kan niks gestuur word nie.',
  'compose.refusal.VALIDATION': 'Iets in hierdie boodskap is nog nie ingevul nie.',
  'compose.refusal.unknown': 'Dit kon nie nou gestuur word nie. Probeer oor \'n oomblik weer.',
  'compose.toast.sent': 'Gestuur aan {{count}}.',
  'compose.toast.partial': 'Gestuur aan {{sent}}, maar {{failed}} het nie deurgekom nie.',
  'compose.toast.noneSent': 'Niks is gestuur nie. Sien hieronder wat gebeur het.',
  'compose.results.allSent.title': 'Alles gestuur',
  'compose.results.allSent.body': 'Dit het al {{count}} van hulle bereik. Hulle antwoorde sal in jou inmandjie land.',
  'compose.results.noneSent.title': 'Niks is gestuur nie',
  'compose.results.noneSent.body': 'Nie een van die {{count}} mense is bereik nie. Wat verkeerd geloop het, wys langs elkeen van hulle.',
  'compose.results.partial.title': 'Sommige hiervan het nie deurgekom nie',
  'compose.results.partial.body': '{{sent}} is bereik en {{failed}} nie. Jy kan dié wat misluk het weer probeer sonder om vir enigiemand twee keer te stuur.',
  'compose.results.delivered': 'Op pad',
  'compose.results.open': 'Maak geselsie oop',
  'compose.results.badge.sent': 'Gestuur',
  'compose.results.badge.failed': 'Nie gestuur nie',
  'compose.results.backToInbox': 'Terug na inmandjie',
  'compose.results.another': 'Skryf nog een',
  'compose.results.retry': 'Probeer {{count}} weer',
  'compose.results.reason.unknown': 'Hierdie een het nie deurgekom nie.',
  'compose.results.reason.OPTED_OUT': 'Hulle het gevra om nie boodskappe te ontvang nie.',
  'compose.results.reason.WINDOW_CLOSED': 'Hulle het nie in die laaste 24 uur aan jou geskryf nie, dus was \'n goedgekeurde sjabloon hiervoor nodig.',
  'compose.results.reason.INSUFFICIENT_CREDITS': 'Daar was nie genoeg krediete oor om hierdie een te stuur nie.',
  'compose.results.reason.PHONE_NOT_FOUND': 'Die nommer waarvandaan dit gestuur is, is nie meer gekoppel nie.',
  'compose.results.reason.CHANNEL_UNREACHABLE': 'WhatsApp kon nie bereik word nie. Niks is aan hulle gestuur nie.',
  'compose.results.reason.SEND_FAILED': 'WhatsApp het hierdie een nie aanvaar nie.',
  'compose.results.reason.META_REFUSED': 'WhatsApp het hierdie een nie aanvaar nie.',
  'compose.results.reason.SEND_UNREADABLE': 'Dit het uitgegaan, maar ons kon nie bevestig wat daarmee gebeur het nie.',
  'compose.number.unnamed': '\'n Nommer wat nog opgestel word',
  'compose.results.reason.ACCOUNT_NOT_PAYABLE': 'WhatsApp het dit afgekeur weens \'n faktureringsprobleem aan ons kant, nie joune nie. Ons maak dit reg, en jy hoef niks by Meta te doen nie.',
  'compose.results.reason.UNDELIVERABLE': 'WhatsApp kon dit nie by daardie nommer aflewer nie.',
  'compose.results.reason.NUMBER_NOT_REGISTERED': 'Die nommer waarvandaan jy gestuur het, is nie by WhatsApp geregistreer nie.',
  'compose.results.reason.MEDIA_NOT_FETCHED': 'WhatsApp kon nie die aangehegte lêer haal nie.',
  'compose.recipients.noNumber': 'Geen foonnommer nie, dus kan WhatsApp hulle nie bereik nie.',
  'compose.results.reason.ACCOUNT_NOT_PAYABLE.customer': 'WhatsApp het dit van die hand gewys omdat jou WhatsApp-rekening geen werkende betaalmetode in Meta het nie. Voeg een by in Meta se betaalinstellings.',
  'compose.results.reason.OWN_ACCOUNT_NOT_ON_PLAN': 'Jou plan sluit nie meer jou eie WhatsApp-rekening in nie. Skuif na Growth of Scale om van hierdie nommer af aan te hou stuur.',
  'compose.results.reason.NUMBER_DAILY_LIMIT': 'Die nommer waarvandaan gestuur is, het WhatsApp se daaglikse limiet op nuwe mense bereik.'
};

export default compose;
